/**
 * Completed Trades — pane 3 of the Trade Hub.
 *
 * The history layer, and the reason the hub exists at all: before it, a finished
 * trade "just sat there". The only read of `trade_proposals` was a per-friend
 * list that mixed pending, agreed and completed together, ordered by recency and
 * capped at 20 — so a completed trade fell off the end once the pair traded
 * enough, and could only be found by navigating back to that friend's compare
 * page. This list is scoped to completed and fetched at a much larger limit.
 *
 * Every row is oriented to the READER (see tradeHistoryOrientation): the same
 * shared proposal row has to say "gave" about what left YOUR bag whichever side
 * of it you are.
 */
import type { ReactNode } from "react";
import { ArrowLeftRight, Check, History } from "lucide-react";

import { speciesName } from "@/data/speciesNames";
import { VariantBadge, isDexVariantKey, variantAlpha, type DexVariantKey } from "@/components/dexVariantVisuals";
import { CompareSprite } from "@/components/trade/CompareSprite";
import { TradePageHero } from "@/components/trade/TradePageHero";
import type { TradePaneKey } from "@/components/trade/TradeHubTabs";
import { orientCompletedTrade } from "@/lib/tradeHistoryOrientation";
import type { TradeHubData } from "@/pages/trades/useTradeHubData";

/** Narrow any stored variant string to a key the badge/colour system knows. */
function variantKey(v: string): DexVariantKey {
  return isDexVariantKey(v) ? v : "pokemon";
}

export function CompletedTradesPane({
  hub,
  tabs,
  goToPane,
}: {
  hub: TradeHubData;
  tabs: ReactNode;
  goToPane: (key: TradePaneKey) => void;
}) {
  const { friendId, friendName, history, historyQ } = hub;

  return (
    <>
      <TradePageHero
        eyebrow={`Trading with ${friendName}`}
        title="Completed Trades."
        greenSuffix="History."
        subtitle={`Every swap you and ${friendName} have finished together, kept for good.`}
      >
        {tabs}
      </TradePageHero>

      {history.length === 0 ? (
        <section className="v2-neon-card-soft mt-4 p-6 text-center" data-testid="completed-empty">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: "var(--surface-dark-10)", border: "1.5px solid var(--accent-fill-glow)" }}
          >
            <History className="w-5 h-5" style={{ color: "var(--accent-fill)" }} />
          </div>
          <h2 className="v2-display text-sm mb-1.5" style={{ color: "var(--text-ink-bright)" }}>
            {historyQ.isLoading ? "Loading history…" : "No completed trades yet"}
          </h2>
          {!historyQ.isLoading && (
            <>
              <p className="text-xs mb-4 leading-relaxed" style={{ color: "var(--text-ink-faint)" }}>
                A trade lands here the moment either of you agrees to it. Anything still waiting on
                a reply is on the Proposed tab.
              </p>
              <button
                onClick={() => goToPane("proposed")}
                data-testid="button-completed-open-proposed"
                className="v2-neon-cta w-full max-w-[260px] mx-auto py-3 text-sm"
              >
                See what's in flight
              </button>
            </>
          )}
        </section>
      ) : (
        <section className="mt-4" data-testid="completed-list-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="v2-display-up text-[11px]" style={{ color: "var(--text-ink-bright-faint)" }}>
              Traded with {friendName}
            </h2>
            <span className="text-[10px] font-mono tabular-nums" style={{ color: "var(--text-ink-faint)" }}>
              {history.length}
            </span>
          </div>
          <ul className="space-y-1.5" data-testid="completed-list">
            {history.map((p) => {
              const { gaveId, gaveVariant, gotId, gotVariant } = orientCompletedTrade(
                p,
                friendId ?? -1,
              );
              const when = new Date(p.updatedAt).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
                year: "numeric",
              });
              return (
                <li
                  key={p.id}
                  className="rounded-[var(--radius-lg)] p-2.5"
                  style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
                  data-testid={`completed-row-${p.id}`}
                >
                  <div className="flex items-center gap-1.5">
                    <Check className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--brand-green)" }} aria-hidden />
                    <span className="text-[10px] font-semibold" style={{ color: "var(--text-tertiary)" }}>
                      {when}
                    </span>
                  </div>
                  {/* Sprites, because a history is something you SCAN rather
                      than read — the art is what makes a past trade findable at
                      a glance, where two species names are just two more lines
                      of text. Same tile treatment as the proposal rows, so a
                      trade looks like itself before and after it is done. */}
                  <div className="mt-1.5 flex items-center gap-2">
                    <span className="min-w-0 flex-1 flex items-center gap-2">
                      <span
                        className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center overflow-clip"
                        style={{
                          background: "var(--surface-sunken)",
                          border: `1px solid ${variantAlpha(variantKey(gaveVariant), 0.4)}`,
                        }}
                      >
                        <CompareSprite id={gaveId} shiny={variantKey(gaveVariant) === "shiny"} size={30} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[9px] font-bold tracking-wider" style={{ color: "var(--text-tertiary)" }}>
                          GAVE
                        </span>
                        <span className="block text-[12px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                          {speciesName(gaveId)}
                        </span>
                        <VariantBadge variant={variantKey(gaveVariant)} />
                      </span>
                    </span>
                    <ArrowLeftRight className="w-4 h-4 shrink-0" style={{ color: "var(--text-tertiary)" }} aria-hidden />
                    <span className="min-w-0 flex-1 flex items-center gap-2">
                      <span
                        className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center overflow-clip"
                        style={{
                          background: "var(--surface-sunken)",
                          border: `1px solid ${variantAlpha(variantKey(gotVariant), 0.4)}`,
                        }}
                      >
                        <CompareSprite id={gotId} shiny={variantKey(gotVariant) === "shiny"} size={30} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[9px] font-bold tracking-wider" style={{ color: "var(--text-tertiary)" }}>
                          GOT
                        </span>
                        <span className="block text-[12px] font-bold truncate" style={{ color: "var(--text-ink-bright)" }}>
                          {speciesName(gotId)}
                        </span>
                        <VariantBadge variant={variantKey(gotVariant)} />
                      </span>
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Clears the tab bar's protruding centre coin. */}
      <div className="mb-8" aria-hidden />
    </>
  );
}
